import React, { forwardRef } from 'react';
import { Box, Text, HStack, VStack, Avatar, Image, Divider } from '@chakra-ui/react';
import { QRCodeCanvas } from 'qrcode.react';
import schoolLogo from 'assets/img/blas-pascal-logo.png';
import { getSchoolInfo } from 'utils/carnetUtils';

const CarnetTemplate = forwardRef(({ customer, qrValue, orientation = 'horizontal', schoolInfo }, ref) => {
  const studentName = `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim();
  const avatarUrl = customer?.meta_data?.find((m) => m.key === 'avatar_url')?.value || null;

  const info = schoolInfo || getSchoolInfo();
  const SCHOOL_NAME = info.name;

  const infoLine = [info.address, info.phone ? `Tel: ${info.phone}` : '']
    .filter(Boolean)
    .join(' · ');

  if (orientation === 'vertical') {
    return (
      <Box
        ref={ref}
        w="260px"
        borderRadius="12px"
        overflow="hidden"
        boxShadow="0 4px 20px rgba(0,0,0,0.15)"
        userSelect="none"
        flexShrink={0}
        bg="#F7FAFC"
      >
        {/* ── Header ── */}
        <Box bg="#2644cd" px="24px" py="24px" position="relative" textAlign="center">
          <Box position="absolute" left={0} top={0} w="100%" h="4px" bg="#2644cd" />
          <Image
            src={schoolLogo}
            alt="Logo"
            boxSize="52px"
            objectFit="contain"
            borderRadius="8px"
            bg="transparent"
            p="2px"
            mx="auto"
            mb="8px"
          />
          <Text fontWeight="bold" fontSize="24px" color="white" letterSpacing="0.5px" noOfLines={1}>
            {SCHOOL_NAME}
          </Text>
          <Text fontSize="20px" color="whiteAlpha.800" mt="3px" noOfLines={1}>
            {infoLine}
          </Text>
        </Box>

        {/* ── Body ── */}
        <VStack spacing="8px" px="18px" py="18px">
          <Avatar
            src={avatarUrl}
            name={studentName}
            w="96px"
            h="96px"
            borderRadius="10px"
            bg="gray.200"
            boxShadow="0 2px 8px rgba(0,0,0,0.10)"
          />
          <VStack spacing="0px">
            <Text fontWeight="bold" fontSize="16px" color="gray.800" textAlign="center" noOfLines={2} lineHeight="1.2" wordBreak="break-word" textTransform="uppercase">
              {customer?.first_name || (!customer?.last_name ? '—' : '')}
            </Text>
            {customer?.last_name && (
              <Text fontWeight="bold" fontSize="16px" color="gray.800" textAlign="center" noOfLines={2} lineHeight="1.2" wordBreak="break-word" textTransform="uppercase">
                {customer.last_name}
              </Text>
            )}
            <Text fontSize="12px" color="gray.500" fontWeight="semibold" letterSpacing="1px" mt="4px">
              ESTUDIANTE
            </Text>
          </VStack>

          <Divider borderColor="gray.200" />

          <Box bg="white" borderRadius="8px" boxShadow="0 2px 8px rgba(0,0,0,0.08)" p="6px">
            {qrValue ? (
              <QRCodeCanvas value={qrValue} size={100} level="M" includeMargin={false} />
            ) : (
              <Box w="100px" h="100px" bg="gray.100" borderRadius="4px" />
            )}
          </Box>
        </VStack>

        {/* ── Footer ── */}
        <Box bg="gray.100" borderTop="1px solid" borderColor="gray.200" py="12px" px="12px" textAlign="center">
          <Text fontSize="9px" color="gray.500" letterSpacing="1px" fontWeight="semibold" lineHeight="1.4">
            DOCUMENTO DE IDENTIFICACIÓN ESTUDIANTIL
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      w="408px"
      borderRadius="12px"
      overflow="hidden"
      boxShadow="0 4px 20px rgba(0,0,0,0.15)"
      userSelect="none"
      flexShrink={0}
      bg="#F7FAFC"
    >
      {/* ── Header ── */}
      <Box bg="#2644cd" px="18px" py="14px" position="relative">
        <Box position="absolute" left={0} top={0} w="5px" h="100%" bg="#2644cd" />
        <HStack justify="space-between" align="center" ml="8px" spacing="8px">
          <VStack align="flex-start" spacing="2px">
            <Text fontWeight="bold" fontSize="18px" color="white" letterSpacing="0.5px" noOfLines={1}>
              {SCHOOL_NAME}
            </Text>
            <Text fontSize="11px" color="whiteAlpha.800" noOfLines={1}>
              {infoLine}
            </Text>
          </VStack>
          <Image
            src={schoolLogo}
            alt="Logo"
            boxSize="56px"
            objectFit="contain"
            borderRadius="6px"
            bg="transparent"
            p="2px"
            flexShrink={0}
          />
        </HStack>
      </Box>

      {/* ── Body: QR | Info | Photo ── */}
      <HStack align="flex-start" spacing={0} px="18px" py="14px">
        {/* QR */}
        <Box
          bg="white"
          borderRadius="8px"
          boxShadow="0 2px 8px rgba(0,0,0,0.08)"
          p="6px"
          flexShrink={0}
          mr="10px"
        >
          {qrValue ? (
            <QRCodeCanvas value={qrValue} size={108} level="M" includeMargin={false} />
          ) : (
            <Box w="108px" h="108px" bg="gray.100" borderRadius="4px" />
          )}
        </Box>

        {/* Student info */}
        <VStack align="flex-start" spacing="0px" flex={1} pt="4px" mr="10px">
          <Text fontWeight="bold" fontSize="13px" color="gray.800" noOfLines={2} lineHeight="1.2" wordBreak="break-word" textTransform="uppercase">
            {customer?.first_name || (!customer?.last_name ? '—' : '')}
          </Text>
          {customer?.last_name && (
            <Text fontWeight="bold" fontSize="13px" color="gray.800" noOfLines={2} lineHeight="1.2" wordBreak="break-word" textTransform="uppercase">
              {customer.last_name}
            </Text>
          )}
          <Text fontSize="15px" color="gray.500" fontWeight="semibold" letterSpacing="2px" mt="8px">
            ESTUDIANTE
          </Text>
        </VStack>

        {/* Photo */}
        <Box flexShrink={0}>
          <Avatar
            src={avatarUrl}
            name={studentName}
            w="90px"
            h="90px"
            borderRadius="8px"
            bg="gray.200"
            boxShadow="0 2px 8px rgba(0,0,0,0.10)"
          />
        </Box>
      </HStack>

      {/* ── Footer ── */}
      <Box bg="gray.100" borderTop="1px solid" borderColor="gray.200" py="7px" px="10px" textAlign="center">
        <Text fontSize="8px" color="gray.500" letterSpacing="1.5px" fontWeight="semibold">
          DOCUMENTO DE IDENTIFICACIÓN ESTUDIANTIL
        </Text>
      </Box>
    </Box>
  );
});

CarnetTemplate.displayName = 'CarnetTemplate';

export default CarnetTemplate;
